import {opacify} from 'polished';
import {SVGAttributes} from 'react';
import {css, keyframes} from 'styled-components';

import {computeHasVisibleScrollbars} from '../../../core/src/helpers/browser/domHelpers';
import {isMacOS} from '../../../core/src/helpers/environment/platformHelpers';
import {blendColors} from '../../../core/src/models/helpers/colorHelpers';
import {ThemeContextType} from '../theme/userThemeProvider';
import {rotate360Animation, shakeCss} from './animations';

/*
 * Re-exported for existing consumers. `animations` has a `.native` counterpart, so importing this
 * module from shared code stays safe on React Native.
 */
export {rotate360Animation, shakeCss};

export const textSelection = () => css`
  -webkit-user-select: text;
  user-select: text;
  cursor: text;

  * {
    -webkit-user-select: initial;
    user-select: initial;
    cursor: inherit;
  }

  [role='button'] {
    cursor: default;
  }
`;

export const noTextSelection = () => css`
  &,
  * {
    -webkit-user-select: none;
    user-select: none;
    cursor: default;
  }
`;

/** Wraps the text on one-line with ellipsis. */
export const ellipsis = (noWrap = true) => css`
  ${noWrap && 'white-space: nowrap;'};
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Truncates the text after `numberOfLines` with an ellipsis.
 *  NOTE: Only works for plain text content.
 *       e.g.
 *            <div>This will work fine</div>
 *            <div>This <b>might</b> have some </br> trouble</div>
 */
export const ellipsisAfterNumberOfLines = (numberOfLines: number, inline = false) => css`
  display: ${inline ? '-webkit-inline-box' : '-webkit-box'};
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${numberOfLines};
  ${ellipsis(false)}
`;

/** Cross-browser reset of the appearance property. */
export function appearanceNone() {
  return css`
    -moz-appearance: none;
    -webkit-appearance: none;
    -ms-progress-appearance: none;
  `;
}

/** If for some reason you're using floats and need your parent element to have the right dimensions. */
export const clearfix = () => css`
  &:before,
  &:after {
    content: '';
    display: table;
  }
  &:after {
    clear: both;
  }
  zoom: 1;
`;

/** Center the content both horizontally and vertically. */
export function center() {
  return css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  `;
}

/** Stretch a single child inside this element. */
export function stretch() {
  return css`
    display: flex;
    align-items: stretch;

    & > * {
      flex: 1;
    }
  `;
}

function scrollbar(trackColor: string, thumbColor: string) {
  // No need for custom scrollbars on Mac, when using the floating/disappearing scrollbars setting.
  if (isMacOS() && !computeHasVisibleScrollbars()) {
    return '';
  }

  return css`
    scrollbar-color: ${thumbColor} ${trackColor};

    @supports (not (scrollbar-color: auto)) and selector(::-webkit-scrollbar) {
      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
        background: transparent;
      }

      &::-webkit-scrollbar-track {
        background: ${trackColor};
      }

      &::-webkit-scrollbar-thumb {
        background-color: ${thumbColor};
        border-radius: 5px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background-color: ${opacify(0.1, thumbColor)};
      }
    }
  `;
}

export function addThemedScrollbarStyles() {
  return css`
    ${(p) => scrollbar(p.theme.scrollbarTrack, p.theme.scrollbarThumb)};
    color-scheme: ${(p) => p.theme.colorScheme};
  `;
}

export function hideScrollbars() {
  return css`
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  `;
}

// https://webaim.org/techniques/css/invisiblecontent/
const visuallyHiddenCss = css`
  clip: rect(1px, 1px, 1px, 1px);
  /* This causes "repaints on scroll" in Chrome and reduces rendering performance! */
  /* clip-path: inset(50%); */
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
`;

/** Visually hide, but allow it to be read by screen readers. */
export const visuallyHidden = () => visuallyHiddenCss;

const highlightPulseAnimation = (p: ThemeContextType) => keyframes`
  0% {
    box-shadow: 0 0 0 0px ${p.theme.palette.pink.shade40}, 0 0 0 2px ${p.theme.palette.pink.shade40};
  }

  50% {
    box-shadow: 0 0 0 15px rgba(0, 0, 0, 0), 0 0 0 2px ${p.theme.palette.pink.shade40};
  }

  100% {
    box-shadow: 0 0 0 15px rgba(0, 0, 0, 0), 0 0 0 2px ${p.theme.palette.pink.shade40};
  }
`;

/** Pulsing border effect to highlight a component. */
export const getHighlightPulseCss = (shouldWrapTight: boolean) => css`
  &:before {
    content: '';
    position: absolute;
    top: ${shouldWrapTight ? '-4px' : '4px'};
    left: ${shouldWrapTight ? '-4px' : '4px'};
    right: ${shouldWrapTight ? '-4px' : '4px'};
    bottom: ${shouldWrapTight ? '-4px' : '4px'};
    pointer-events: none;
    border-radius: 20px;
  }
`;

export const getHighlighterCss = () => css`
  &:before {
    content: '';
    position: absolute;
    z-index: 1;
    top: 2px;
    left: 2px;
    right: 2px;
    bottom: 2px;
    pointer-events: none;
    animation: ${highlightPulseAnimation} 2s infinite;
  }
`;

/** Flex display with vertically aligned elements */
export const flexCenter = css`
  display: flex;
  align-items: center;
`;

// Based off https://github.com/kovart/dashed-border-generator
interface BuildDashedBorderBackgroundOptions {
  strokeColor: string;
  dashArray: string;
  strokeWidth: number;
  dashOffset?: number;
  borderRadius?: number;
  strokeLinecap?: SVGAttributes<SVGRectElement>['strokeLinecap'];
  strokeLinejoin?: SVGAttributes<SVGRectElement>['strokeLinejoin'];
}
export function buildDashedBorderBackground(options: BuildDashedBorderBackgroundOptions) {
  const baseString = `<svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
  <rect
    width="100%"
    height="100%"
    fill="none"
    rx="${options.borderRadius ?? 0}"
    ry="${options.borderRadius ?? 0}"
    stroke="${options.strokeColor}"
    stroke-width="${options.strokeWidth * 2}"
    stroke-dasharray="${options.dashArray}"
    ${(options.dashOffset && `stroke-dashoffset="${options.dashOffset}"`) || ''}
    ${(options.strokeLinecap && `stroke-linecap="${options.strokeLinecap}"`) || ''}
    ${(options.strokeLinejoin && `stroke-linejoin="${options.strokeLinejoin}"`) || ''}
  />
</svg>
`;

  const compressed = encodeURIComponent(baseString.replace(/\s+/g, ' ').replace(/"/g, "'"));

  return css`
    // prettier-ignore
    background-image: url("data:image/svg+xml,${compressed}");
    border-radius: ${options.borderRadius ?? 0}px;
  `;
}

export const userHtmlAnchorStyles = css`
  a {
    color: ${(p) => p.theme.typography.shades.interactive};
  }

  a,
  a * {
    cursor: pointer;
  }

  a:hover,
  a:visited,
  a:active {
    color: ${(p) => p.theme.typography.shades['interactive-hover']};
  }
`;

export function transparent(isTransparent?: boolean) {
  return css`
    opacity: ${isTransparent ?? true ? '0.5' : '1'};
  `;
}

export const disabledCss = css`
  pointer-events: none;
  opacity: 0.5;
  filter: grayscale(1);
`;

export function addCardStyles(background?: string) {
  return css`
    border-radius: 10px;
    background: ${(p) => background ?? p.theme.background.surface.primary};
    border: 1px solid ${(p) => p.theme.border.tertiary};
  `;
}

/** Rounded inner-stroke, background and box-shadow states for composer-like input containers. */
export function addComposerInputStyles() {
  return css`
    background-color: ${(p) =>
      blendColors(p.theme.background.surface.primary, p.theme.input.inverseBackground)};
    box-shadow:
      inset 0 0 0 1px ${(p) => p.theme.border.tertiary},
      0 2px 8px 0px rgba(0, 0, 0, 0.06);
    transition: box-shadow 150ms ease;

    &:hover {
      box-shadow:
        inset 0 0 0 1px ${(p) => p.theme.border.secondary},
        0 2px 8px 0px rgba(0, 0, 0, 0.06);
    }

    &:focus-within,
    &:focus-visible {
      background-color: ${(p) =>
        blendColors(p.theme.background.surface.primary, p.theme.input.inverseBackgroundHover)};
      box-shadow:
        inset 0 0 0 1px ${(p) => p.theme.border.focusInverse},
        0 2px 10px 0px rgba(0, 0, 0, 0.1);
      transition: box-shadow 250ms ease;
    }
  `;
}

/** Defines styles to be used with the `prefers-reduced-motion` media query. Use your best judgement when writing motion-safe alternative styles. */
export function motionStyles(styles: ReturnType<typeof css>, safeStyles?: ReturnType<typeof css>) {
  return css`
    @media (prefers-reduced-motion: no-preference) {
      ${styles};
    }

    ${safeStyles &&
    css`
      @media (prefers-reduced-motion: reduce) {
        ${safeStyles};
      }
    `};
  `;
}

interface TextShimmerStylesOptions {
  /** How long a single shimmer animation takes to cross the text. */
  animationDurationMs: number;
  /**
   * The time to wait after a shimmer animation before starting the next one,
   * to better match the cadence of other animations.
   */
  pauseBetweenIterationsMs: number;
  /**
   * How long to hold before the first shimmer animation, to line its phase up with an
   * animation that starts alongside it but reaches its own cue later.
   */
  animationDelayMs: number;
  spreadPx: number;
  textColor: string;
  textShimmerColor: string;
}

export function textShimmerStyles(options?: Partial<TextShimmerStylesOptions>) {
  const {
    animationDurationMs = 1500,
    pauseBetweenIterationsMs = 750,
    animationDelayMs = 0,
    spreadPx = 40,
  } = (options ?? {}) satisfies typeof options;

  const animationCompletionPercentage = `${Math.round(
    (animationDurationMs / (animationDurationMs + pauseBetweenIterationsMs)) * 100,
  )}%`;

  const shimmer = keyframes`
    0% {
            background-position: top right;
    }
    ${animationCompletionPercentage} {
            background-position: top left;
    }
    100% {
            background-position: top left;
    }
  `;

  return css`
    --spread: ${spreadPx}px;

    color: transparent;

    --shimmer-text-color: ${(p) => options?.textColor || p.theme.typography.shades.secondary};
    --shimmer-text-shimmer-color: lch(from var(--shimmer-text-color) calc(l + 50) c h);

    background: linear-gradient(
        135deg,
        #0000 calc(50% - var(--spread)),
        ${options?.textShimmerColor ?? 'var(--shimmer-text-shimmer-color)'},
        #0000 calc(50% + var(--spread))
      ),
      linear-gradient(
        ${(p) => options?.textColor || p.theme.typography.shades.secondary},
        ${(p) => options?.textColor || p.theme.typography.shades.secondary}
      );
    background-clip: text;
    background-size:
      400% 100%,
      auto;
    background-repeat: no-repeat;
    background-position: 0 0;
    animation: ${shimmer} ${animationDurationMs + pauseBetweenIterationsMs}ms linear ${animationDelayMs}ms
      infinite;
  `;
}

/*
 * Quick actions transition styles.
 */

export const quickActionsAnimateInDurationMs = 150;
export const quickActionsAnimateOutDurationMs = 100;
export const quickActionsAnimateInDelayMs = 80;
export const quickActionsAnimationCurve = 'cubic-bezier(0.4, 0, 0.2, 1)';

function buildQuickActionsTransitionValue(transitionProperty: string, isAnimatingIn: boolean) {
  return isAnimatingIn
    ? `${transitionProperty} ${quickActionsAnimateInDurationMs}ms ${quickActionsAnimateInDelayMs}ms
          ${quickActionsAnimationCurve}`
    : `${transitionProperty} ${quickActionsAnimateOutDurationMs}ms ${quickActionsAnimationCurve}`;
}

export function addQuickActionsTransitionStyles(
  transitionProperties: ReadonlyArray<string>,
  isAnimatingIn: boolean,
) {
  return css`
    transition: ${transitionProperties
      .map((transitionProperty) => buildQuickActionsTransitionValue(transitionProperty, isAnimatingIn))
      .join(', ')};
  `;
}
